import selectNodeInFolderForest from './selectNodeInFolderForest';
import onAfterSelectingNode from './helpers/onAfterSelectingNode';
import { getSelectedNode } from 'owa-mail-folder-forest-store';
import type { GroupFolderForestNode } from 'owa-favorites-types';
import { type FolderForestNodeType } from 'owa-favorites-types';
import type { FolderForestTreeType } from 'owa-graph-schema';
import { mutatorAction } from 'satcheljs';
import { lazySelectGroup } from '../lazyFunctions';
import { type GroupFolderWellKnownName } from 'owa-folders-constants';
import { getGroupFolderId } from 'owa-groups-shared-folders-store';
import type { MailboxInfo } from 'owa-client-types';
import transformGroupSmtp from 'owa-group-common/lib/utils/transformGroupSmtp';

/**
 * General select group folder action
 * @param groupId the groupId
 * @param groupFolderId the group folder id
 * @param treeType the type of folder tree
 */
function selectGroupFolderInternal(
    groupId: string,
    groupFolderId: string,
    treeType: FolderForestTreeType,
    mailboxInfo: MailboxInfo
): Promise<void> {
    return selectNodeInFolderForest(<GroupFolderForestNode>{
        id: groupFolderId,
        type: 6,
        treeType,
        groupId,
        mailboxInfo,
    });
}

const updateNodeMutator = mutatorAction(
    'updateNodeMutator',
    (
        node: GroupFolderForestNode,
        updates: Pick<GroupFolderForestNode, 'id' | 'groupId' | 'treeType'>
    ) => {
        node.treeType = updates.treeType;
        node.groupId = updates.groupId;
        node.id = updates.id;
    }
);

/**
 * Select a group folder
 * @param groupId the groupId
 * @param groupFolderId the group folder Id
 * @param treeType the type of folder tree
 */
export default async function selectGroupFolder(
    groupId: string,
    groupFolderId: string,
    treeType: FolderForestTreeType,
    mailboxInfo: MailboxInfo
): Promise<void> {
    let folderId = groupFolderId;
    if (groupFolderId === 'inbox' || groupFolderId === 'deleteditems') {
        folderId = getGroupFolderId(groupId, groupFolderId, mailboxInfo);
    }
    const currentNode = getSelectedNode() as GroupFolderForestNode;

    if (
        currentNode.mailboxInfo &&
        currentNode.mailboxInfo.userIdentity.toLowerCase() !==
            mailboxInfo.userIdentity.toLowerCase()
    ) {
        // Switching between different mailboxes
        await lazySelectGroup.importAndExecute(groupId, treeType, mailboxInfo);
        selectGroupFolderInternal(groupId, folderId, treeType, mailboxInfo);
    } else if (
        currentNode.type === 6 &&
        currentNode.groupId &&
        transformGroupSmtp(currentNode.groupId) === transformGroupSmtp(groupId) &&
        currentNode.id &&
        currentNode.id.toLowerCase() === folderId.toLowerCase() &&
        currentNode.treeType === treeType
    ) {
        // Do not perform the rest of selecting group folder logic when
        // user navigates to the node with the same id
        updateNodeMutator(currentNode, {
            id: folderId,
            groupId,
            treeType,
        });
    } else if (
        currentNode.type === 6 &&
        currentNode.groupId &&
        transformGroupSmtp(currentNode.groupId) === transformGroupSmtp(groupId) &&
        currentNode.id &&
        currentNode.id.toLowerCase() !== folderId.toLowerCase() &&
        currentNode.treeType === treeType
    ) {
        // Same group different folder within the same treeType
        selectGroupFolderInternal(groupId, folderId, treeType, mailboxInfo);
    } else {
        // Same treetype different group, or different treetype
        await lazySelectGroup.importAndExecute(groupId, treeType, mailboxInfo);
        selectGroupFolderInternal(groupId, folderId, treeType, mailboxInfo);
    }
    onAfterSelectingNode();
}
